/**
 * Farm Selector Dropdown for the top bar in FarmDashboardLayout.
 * Pill-shaped dropdown showing current farm name with ability to switch farms.
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { fetchFarmList, selectFarmList } from '@/store/slices/farmSlice';

const FarmSelectorDropdown: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { farmId } = useParams<{ farmId: string }>();
  const dispatch = useAppDispatch();
  // farmList is sorted server-side by name and is unpaginated, so no
  // local re-sorting or windowing is needed.
  const farms = useAppSelector(selectFarmList);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (farms.length === 0) {
      dispatch(fetchFarmList());
    }
  }, [dispatch, farms.length]);

  const currentFarm = farms.find(f => f.id === farmId);

  const handleFarmSelect = (selectedFarmId: string) => {
    setAnchorEl(null);
    if (selectedFarmId !== farmId) {
      // Replace the current farmId in the path to stay on the same page
      const newPath = location.pathname.replace(`/farms/${farmId}`, `/farms/${selectedFarmId}`);
      navigate(newPath);
    }
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDown />}
        sx={{
          borderRadius: 20,
          px: 2.5,
          py: 0.75,
          bgcolor: 'action.hover',
          color: 'text.primary',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          '&:hover': {
            bgcolor: 'action.selected',
          },
        }}
      >
        {currentFarm?.name || 'Select farm'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { minWidth: 200, maxHeight: 300 },
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        {farms.map((farm) => (
          <MenuItem
            key={farm.id}
            selected={farm.id === farmId}
            onClick={() => handleFarmSelect(farm.id)}
          >
            <ListItemText>
              <Typography variant="body2" fontWeight={farm.id === farmId ? 600 : 400}>
                {farm.name}
              </Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FarmSelectorDropdown;
